import React from 'react';
import { intl } from 'i18n';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { ROUTES } from '../../../../global/constants';
import {getLoggedInStatus, getUserCareerProfile} from '../../../../auth/selectors';
import { CareerOverviewCard } from 'labxchange-client';
import Navbar from '../../CareerNavbar/CareerNavbar';
import { RegistrationGate } from 'ui/components/RegistrationGate';
import uiMessages from 'ui/components/displayMessages';
import { WrappedMessage } from 'utils';

const messages = {
    savedCareersTitle: {
        id: 'savedCareersTitle',
        defaultMessage: 'Saved Careers',
        description: 'Title for the saved careers page.',
    },
    titleColumn: {
        id: 'titleColumn',
        defaultMessage: 'Career',
        description: 'Column header for career title.',
    },
    categoryColumn: {
        id: 'categoryColumn',
        defaultMessage: 'Career cluster',
        description: 'Column header for category.',
    },
    descriptionColumn: {
        id: 'descriptionColumn',
        defaultMessage: 'Description',
        description: 'Column header for career description.',
    },
    minQualificationColumn: {
        id: 'minQualificationColumn',
        defaultMessage: 'Minimum education',
        description: 'Column header for minimum qualification.',
    },
    salaryColumn: {
        id: 'salaryColumn',
        defaultMessage: 'Salary',
        description: 'Column header for career salary.',
    },
    outlookColumn: {
        id: 'outlookColumn',
        defaultMessage: 'Demand',
        description: 'Column header for career outlook.',
    },
    noIconAvailable: {
        id: 'noIconAvailable',
        defaultMessage: 'N/A',
        description: 'Text displayed when no icon is available.',
    },
    noDataAvailable: {
        id: 'noDataAvailable',
        defaultMessage: 'N/A',
        description: 'Text displayed when no information is available.',
    },
    noSavedCareers: {
        id: 'noSavedCareers',
        defaultMessage: 'You have no saved careers.',
        description: 'Message displayed when there are no saved careers.',
    },
    noDefinedCategory: {
        id: 'noDefinedCategory',
        defaultMessage: 'Not Defined',
        description: 'Message displayed when there are no categories defined for a career.',
    },
    registrationGateTitle: {
        id: 'registrationGateTitle',
        defaultMessage: 'You are not logged in',
        description: 'Title for the registration gate.',
    },
    registrationGateBody: {
        id: 'registrationGateBody',
        defaultMessage: 'Please log in to view your saved careers.',
        description: 'Body for the registration gate.',
    },
};

const SavedCareersPage: React.FC = () => {
    const isLoggedIn = useSelector(getLoggedInStatus);
    const careers = useSelector(getUserCareerProfile)?.careers || [];

    const getDemandTrajectory = (overviewCards: CareerOverviewCard[]) => {
        const outlookCard = overviewCards.find(card => card.cardType === 'outlook');
        return outlookCard?.cardLabel || null;
    };
    const getSalaryRange = (overviewCards: CareerOverviewCard[]) => {
        const outlookCard = overviewCards.find(card => card.cardType === 'salary');
        return outlookCard?.cardLabel || null;
    };

    return (
        <>
            {isLoggedIn ?
                <>
                    <Navbar/>
                    <div className='saved-careers-main-container'>
                        <div className='saved-careers-page'>
                            <h2 className='saved-careers-title'>
                                <WrappedMessage message={messages.savedCareersTitle}/>
                            </h2>

                            {careers.length === 0 ? (
                                <p className='no-saved-careers-message'>
                                    <WrappedMessage message={messages.noSavedCareers}/>
                                </p>
                            ) : (
                                <div className='career-table-container'>
                                    <table className='career-table'>
                                        <thead>
                                        <tr>
                                            <th><WrappedMessage message={messages.titleColumn}/></th>
                                            <th><WrappedMessage message={messages.categoryColumn}/></th>
                                            <th><WrappedMessage message={messages.minQualificationColumn}/></th>
                                            <th><WrappedMessage message={messages.salaryColumn}/></th>
                                            <th><WrappedMessage message={messages.outlookColumn}/></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {careers.map((career) => (
                                            <tr key={career.slug}>
                                                <td>
                                                    <Link
                                                        to={ROUTES.CareerExplorer.CAREER_DETAILS_SLUG(career.slug)}
                                                        className='career-title-link'
                                                    >
                                                        {career.title}
                                                    </Link>
                                                </td>
                                                <td>
                                                    {career.subcategories?.length ? (
                                                        <>
                                                            {career.subcategories[0].category.categoryName} <span> - </span>
                                                            {career.subcategories.map((sub) => sub.subcategoryName).join(', ')}
                                                        </>
                                                    ) : (
                                                        <WrappedMessage message={messages.noDefinedCategory} />
                                                    )}
                                                </td>
                                                <td>{career.minimumQualification.qualificationName}</td>
                                                <td>
                                                    {career.overviewCards ? getSalaryRange(career.overviewCards) : <WrappedMessage message={messages.noDataAvailable}/>}
                                                </td>
                                                <td>{career.overviewCards ? getDemandTrajectory(career.overviewCards)
                                                    : <WrappedMessage message={messages.noDataAvailable}/>}
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                </>
                : <RegistrationGate
                    title={intl.formatMessage(messages.registrationGateTitle)}
                    body={intl.formatMessage(messages.registrationGateBody)}
                    primaryButtonText={uiMessages.uiSignUp}
                    secondaryButtonText={uiMessages.uiSignIn}
                    hideLayout={true}
                    contentInBody={true}
                    image='/assets/images/access.svg'
                />
            }
        </>

    );
};

export default SavedCareersPage;
